import { DataMatchPair, DataMatchPersonSnapshot, DataMatchRunResult, type DataMatchCompleteRequestBody, type DataMatchRuleBody } from "../../../../models/DataMatchResult";
import {
    extractIncomingColumns,
    getCentralColumnValue,
    getDefaultMatchRows,
    getProviderColumnValue,
    misCentralColumns,
    newMatchRowId,
    type MatchFilterRow,
} from "./column-options";

// ─── Types ──────────────────────────────────────────────────────────────────────

export type SortState = { column: string; direction: 'asc' | 'desc' } | null;

export type MatchedSortState = { side: 'central' | 'provider'; column: string; direction: 'asc' | 'desc' } | null;

export type UnmatchedColumn = {
    key: string;
    label: string;
    getValue: (r: DataMatchPersonSnapshot) => string;
};

/** One undo step: table state + match filters as they were after the previous successful run (defaults before the first). */
export type MatchUndoFrame = {
    runResult: DataMatchRunResult;
    matchRows: MatchFilterRow[];
};

// ─── Constants ──────────────────────────────────────────────────────────────────

export const DATA_MATCH_STATE_KEY = "dataMatchState";

export const CENTRAL_UNMATCHED_COLUMNS: UnmatchedColumn[] = misCentralColumns.map((c) => ({
    key: c.value,
    label: c.label,
    getValue: (r) => getCentralColumnValue(c.value, r),
}));

export const PROVIDER_UNMATCHED_COLUMNS: UnmatchedColumn[] = extractIncomingColumns.map((c) => ({
    key: c.value,
    label: c.label,
    getValue: (r) => getProviderColumnValue(c.value, r),
}));

/** Column keys to search across for name-based filtering. */
const NAME_SEARCH_KEYS = ['FIRST_NAME', 'SURNAME', 'LEGAL_FIRST_NAME', 'LEGAL_SURNAME'];

export const CENTRAL_COLUMN_HEADER: Record<string, string> = {
    UPN: "UPN",
    FIRSTNAME: "First Name",
    LASTNAME: "Last Name",
    DOB: "DOB",
    EMAIL: "Email",
};

// ─── Pure helpers ───────────────────────────────────────────────────────────────

export function matchesNameSearch(
    person: DataMatchPersonSnapshot,
    searchTerm: string,
    getColumnValue: (columnValue: string, person: DataMatchPersonSnapshot) => string,
): boolean {
    if (!searchTerm) return true;
    const lower = searchTerm.toLowerCase();
    return NAME_SEARCH_KEYS.some((key) => {
        const val = getColumnValue(key, person);
        return val !== '—' && val.toLowerCase().includes(lower);
    });
}

export function nextSortState(prev: SortState, column: string): SortState {
    if (prev?.column === column) {
        return prev.direction === 'asc' ? { column, direction: 'desc' } : null;
    }
    return { column, direction: 'asc' };
}

/** Short label for link confirmation (names, then UPN / IDs). */
export function formatSnapshotDisplayName(p: DataMatchPersonSnapshot): string {
    const first = (p.firstName ?? "").trim();
    const last = (p.lastName ?? "").trim();
    if (first || last) {
        return [first, last].filter(Boolean).join(" ").trim();
    }
    if (p.upn) {
        return p.upn;
    }
    if (p.misId) {
        return p.misId;
    }
    if (p.crbcId) {
        return `CRBC ${p.crbcId}`;
    }
    if (p.clientId) {
        return `Client ${p.clientId}`;
    }
    return "Unknown";
}

export function cloneMatchRows(rows: MatchFilterRow[]): MatchFilterRow[] {
    return rows.map((r) => ({ ...r }));
}

export function buildDefaultMatchRowsState(): MatchFilterRow[] {
    return getDefaultMatchRows().map((d) => ({
        id: newMatchRowId(),
        centralColumn: d.centralColumn,
        providerColumn: d.providerColumn,
        matchScore: d.matchScore,
    }));
}

export function initVisibility(values: { value: string }[]) {
    return Object.fromEntries(values.map((c) => [c.value, true])) as Record<string, boolean>;
}

export function matchedPairKey(pair: DataMatchPair, idx: number) {
    return `m-${pair.central?.crbcId ?? "c"}-${pair.provider?.misId ?? "p"}-${pair.provider?.clientId ?? ""}-${idx}`;
}

export function exportUnmatchedCsv(rows: DataMatchPersonSnapshot[], columns: UnmatchedColumn[], filename: string) {
    const escapeCsvCell = (value: string): string => {
        if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes('\r')) {
            return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
    };
    const header = columns.map((c) => escapeCsvCell(c.label)).join(',');
    const dataRows = rows.map((r) =>
        columns.map((c) => escapeCsvCell(c.getValue(r))).join(',')
    );
    const csvContent = [header, ...dataRows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
}

// ─── Complete-payload helpers ───────────────────────────────────────────────────

function snapshotForCompleteApi(s: DataMatchPersonSnapshot): Record<string, unknown> {
    const o: Record<string, unknown> = {};
    if (s.crbcId) {
        o.crbcId = s.crbcId;
    }
    if (s.clientId) {
        o.clientId = s.clientId;
    }
    if (s.misId) {
        o.misId = s.misId;
    }
    if (s.upn != null && s.upn !== "") {
        o.upn = s.upn;
    }
    if (s.firstName != null && s.firstName !== "") {
        o.firstName = s.firstName;
    }
    if (s.lastName != null && s.lastName !== "") {
        o.lastName = s.lastName;
    }
    if (s.dateOfBirth) {
        o.dateOfBirth = s.dateOfBirth;
    }
    if (s.email) {
        o.email = s.email;
    }
    if (s.displayFields && Object.keys(s.displayFields).length > 0) {
        o.displayFields = s.displayFields;
    }
    return o;
}

function matchRowsToCompleteRuleBodies(rows: MatchFilterRow[]): DataMatchRuleBody[] {
    return rows.map((r) => ({
        centralField: String(r.centralColumn ?? "").trim(),
        providerField: String(r.providerColumn ?? "").trim(),
        thresholdPercent: r.matchScore,
    }));
}

export function buildCompletePayload(
    matchClientId: string,
    misConfigurationId: number,
    run: DataMatchRunResult,
    matchedByManualCount: number,
    matchRows: MatchFilterRow[],
): DataMatchCompleteRequestBody {
    return {
        clientId: matchClientId,
        misConfigurationId,
        matchedByManualCount,
        matchRules: matchRowsToCompleteRuleBodies(matchRows),
        matchedPairs: run.matchedPairs.map((p) => ({
            central: snapshotForCompleteApi(p.central),
            provider: snapshotForCompleteApi(p.provider),
        })),
        unmatchedCentral: run.unmatchedCentral.map(snapshotForCompleteApi),
        unmatchedProvider: run.unmatchedProvider.map(snapshotForCompleteApi),
    };
}
